import { StripeError } from '@stripe/stripe-js';
import {
  BILLING_UNKNOWN_ERROR,
  BillingErrorPayload,
  normalizeBillingError,
} from '@utils/billingErrors';

export const ADD_PAYMENT_METHOD_FALLBACK_ERROR =
  'We could not add your payment method. Please try a different card or contact support@theinformation.com.';

export const BILLING_ADDRESS_FALLBACK_ERROR =
  'We could not save your billing address. Please check the address and try again.';

export const SUBSCRIPTION_PAYMENT_FALLBACK_ERROR =
  'We could not process your payment. Please try a different card or contact support@theinformation.com.';

export const CLIENT_BILLING_FALLBACK_ERROR = 'An unexpected error occurred. Please try again.';

type SetupIntentLike = {
  last_setup_error?: {
    message?: string | null;
  } | null;
} | null;

type StripeErrorLike = {
  code?: string;
  decline_code?: string;
  message?: string;
  type?: string;
} | null;

export function clientBillingError(
  message?: string,
  fallback: string = CLIENT_BILLING_FALLBACK_ERROR
): BillingErrorPayload {
  return {
    ...BILLING_UNKNOWN_ERROR,
    message: message || fallback,
    cta_type: 'try_again',
    cta_link: null,
  };
}

export function stripeSetupErrorPayload(
  error?: StripeError | null,
  setupIntent?: SetupIntentLike
): BillingErrorPayload {
  return clientBillingError(
    error?.message || setupIntent?.last_setup_error?.message,
    ADD_PAYMENT_METHOD_FALLBACK_ERROR
  );
}

export function addPaymentMethodErrorPayload(
  error?: { message?: string } | null
): BillingErrorPayload {
  return clientBillingError(error?.message, ADD_PAYMENT_METHOD_FALLBACK_ERROR);
}

export function billingAddressErrorPayload(): BillingErrorPayload {
  return clientBillingError(BILLING_ADDRESS_FALLBACK_ERROR);
}

export function stripeErrorPayload(stripeError?: StripeErrorLike): BillingErrorPayload {
  const isShowable = stripeError?.type === 'card_error' && Boolean(stripeError.message);

  return {
    ...BILLING_UNKNOWN_ERROR,
    error_code: 'billing_payment_failed',
    message: isShowable ? stripeError.message : SUBSCRIPTION_PAYMENT_FALLBACK_ERROR,
    cta_type: 'try_again',
    cta_link: null,
    recoverable: true,
    stripe_code: stripeError?.decline_code ?? stripeError?.code ?? null,
    stripe_message: stripeError?.message ?? null,
    flow: 'subscribe',
  };
}

export { normalizeBillingError as apiErrorPayload };
